import React, { memo, useRef, useCallback, useEffect } from 'react';
import { Grid } from 'react-window';
import AutoSizer from 'components/AutoSizer';
import { useInfiniteLoader } from 'react-window-infinite-loader';
import { subscribe } from 'lib/EventBuser';
import { REFRESH_TABLE } from 'lib/events';
import CellComponent from './CellComponent';

import './styles.scss';

const LOADING = 1;
const LOADED = 2;

function calculateGrid(total, minWidth, maxWidth, containerWidth) {
    if (!total || !containerWidth) return { columnCount: 1, rowCount: 0, columnWidth: minWidth };

    let columnCount = Math.floor(containerWidth / minWidth);
    columnCount = Math.max(1, Math.min(columnCount, total));
    let columnWidth = containerWidth / columnCount;
    if (columnWidth > maxWidth) columnWidth = maxWidth;
    const rowCount = Math.ceil(total / columnCount);
    return { columnCount, rowCount, columnWidth };
}

const VirtualizedGrid = memo(
    ({
        entity,
        total,
        data = [],
        renderRow,
        onLoad,
        hasNextPage,
        rowHeight = 326,
        minWidth = 262,
        maxWidth = 300,
    }) => {
        const gridRef = useRef();
        const itemStatusMap = useRef({});
        const isFirstRender = useRef(true);
        const columnCountRef = useRef(1);

        useEffect(() => {
            if (!isFirstRender.current) return;
            isFirstRender.current = false;
            onLoad({ resetOffset: true });
        }, [onLoad]);

        useEffect(() => {
            if (entity?.entity) {
                return subscribe(`${REFRESH_TABLE}_${entity.entity}`, (lazyInfo) => {
                    itemStatusMap.current = {};
                    onLoad({ resetOffset: true, ...(lazyInfo || {}) });
                    gridRef.current?.scrollToCell({ rowIndex: 0, columnIndex: 0 });
                });
            }
        }, [entity, onLoad]);

        const isRowLoaded = useCallback((index) => !!itemStatusMap.current[index], []);

        const loadMoreRows = useCallback(
            (startIndex, stopIndex) => {
                if (startIndex === 0 || !hasNextPage) return Promise.resolve();

                for (let index = startIndex; index <= stopIndex; index++) {
                    itemStatusMap.current[index] = LOADING;
                }

                return onLoad().then(() => {
                    for (let index = startIndex; index <= stopIndex; index++) {
                        itemStatusMap.current[index] = LOADED;
                    }
                });
            },
            [hasNextPage, onLoad],
        );

        const onRowsRendered = useInfiniteLoader({
            isRowLoaded,
            loadMoreRows,
            rowCount: total || data.length || 1,
        });

        const handleCellsRendered = useCallback(
            (visibleCells, allCells) => {
                const columnCount = columnCountRef.current;
                onRowsRendered({
                    startIndex: allCells.rowStartIndex * columnCount,
                    stopIndex: allCells.rowStopIndex * columnCount + columnCount - 1,
                });
            },
            [onRowsRendered],
        );

        const renderLazyGrid = useCallback(
            (width, height) => {
                if (!width || !height) return null;

                const realWidth = width - 16;
                const gridData = calculateGrid(
                    total || data?.length,
                    minWidth,
                    maxWidth,
                    realWidth,
                );

                const { columnCount, rowCount, columnWidth } = gridData;
                columnCountRef.current = columnCount;

                const cellProps = {
                    data,
                    renderRow,
                    columnCount,
                };

                return (
                    <div style={{ width: realWidth, height }}>
                        <Grid
                            className="fm-virtualized-grid"
                            columnWidth={columnWidth}
                            rowHeight={rowHeight}
                            columnCount={columnCount}
                            rowCount={rowCount}
                            cellProps={cellProps}
                            cellComponent={CellComponent}
                            gridRef={gridRef}
                            onCellsRendered={handleCellsRendered}
                            overscanCount={2}
                        />
                    </div>
                );
            },
            [data, handleCellsRendered, maxWidth, minWidth, renderRow, rowHeight, total],
        );

        if (!renderRow || !data?.length) return null;

        return <AutoSizer>{({ width, height }) => renderLazyGrid(width, height)}</AutoSizer>;
    },
);

export default VirtualizedGrid;
